// color-exempt-file — retokenisation Phase 6 ui-themes : dark:bg-[#0a0a0a] preserve le rendu dark historique (voir memoire ui-themes)
import React from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@core/components/ui/button";
import { Link } from "react-router-dom";
import { TopNav } from "@core/components/layout/TopNav";
import { PublicFooter } from "../components/PublicFooter";
import { SEOHead } from "../components/SEOHead";
import {
  Calendar,
  CheckCircle2,
  Users,
  Zap,
  Heart,
  Shield,
  Rocket,
} from "lucide-react";

export default function About() {
  const { t } = useTranslation(["polls", "common", "errors"]);

  return (
    <div className="min-h-screen-safe bg-background dark:bg-[#0a0a0a] font-sans text-foreground">
      <SEOHead
        title={t("polls:about_seo_title")}
        description={t("polls:about_seo_description")}
      />
      <TopNav />

      {/* Hero Section */}
      <div className="pt-32 pb-16 text-center px-4">
        <h1 className="text-4xl md:text-6xl font-extrabold mb-6 text-primary">
          NAJUIA
        </h1>
        <p className="text-xl md:text-2xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
          {t("polls:about.hero_subtitle")}
        </p>
      </div>

      {/* Mission Section */}
      <div className="container mx-auto px-4 py-12">
        <div className="bg-card rounded-2xl p-8 md:p-12 border border-border shadow-sm backdrop-blur-sm max-w-5xl mx-auto">
          <div className="flex flex-col md:flex-row gap-8 items-center">
            <div className="w-16 h-16 bg-primary/20 rounded-full flex items-center justify-center shrink-0 ring-1 ring-primary/40">
              <Rocket className="w-8 h-8 text-primary" />
            </div>
            <div className="text-left">
              <h2 className="text-3xl font-bold text-foreground mb-4">
                {t("polls:about.mission_title")}
              </h2>
              <p className="text-muted-foreground text-lg leading-relaxed">
                {t("polls:about.mission_text")}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Nos Valeurs Grid */}
      <div className="container mx-auto px-4 py-16 max-w-6xl">
        <h2 className="text-3xl font-bold text-center mb-12 text-foreground">
          {t("polls:about.values_title")}
        </h2>
        <div className="grid md:grid-cols-3 gap-8">
          {/* Efficacité */}
          <div className="bg-muted/30 p-8 rounded-xl border border-border hover:bg-accent transition-colors">
            <div className="w-12 h-12 bg-success/10 rounded-lg flex items-center justify-center mb-6">
              <Zap className="w-6 h-6 text-success" />
            </div>
            <h3 className="text-xl font-bold text-foreground mb-3">
              {t("polls:about.value_efficiency_title")}
            </h3>
            <p className="text-muted-foreground">
              {t("polls:about.value_efficiency_text")}
            </p>
          </div>

          {/* IA-First */}
          <div className="bg-muted/30 p-8 rounded-xl border border-border hover:bg-accent transition-colors">
            <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-6">
              <Rocket className="w-6 h-6 text-primary" />
            </div>
            <h3 className="text-xl font-bold text-foreground mb-3">
              {t("polls:about.value_ai_title")}
            </h3>
            <p className="text-muted-foreground">
              {t("polls:about.value_ai_text")}
            </p>
          </div>

          {/* Simplicité */}
          <div className="bg-muted/30 p-8 rounded-xl border border-border hover:bg-accent transition-colors">
            <div className="w-12 h-12 bg-warning/10 rounded-lg flex items-center justify-center mb-6">
              <CheckCircle2 className="w-6 h-6 text-warning" />
            </div>
            <h3 className="text-xl font-bold text-foreground mb-3">
              {t("polls:about.value_simplicity_title")}
            </h3>
            <p className="text-muted-foreground">
              {t("polls:about.value_simplicity_text")}
            </p>
          </div>
        </div>
      </div>

      {/* Stats / Story */}
      <div className="container mx-auto px-4 py-12 mb-16">
        <div className="grid md:grid-cols-2 gap-12 items-center max-w-5xl mx-auto">
          <div className="space-y-6">
            <h2 className="text-3xl font-bold text-foreground">
              {t("polls:about.story_title")}
            </h2>
            <p className="text-muted-foreground leading-relaxed">
              {t("polls:about.story_p1")}
            </p>
            <p className="text-muted-foreground leading-relaxed">
              {t("polls:about.promise_prefix")}{" "}
              <strong>{t("polls:about.promise_highlight")}</strong>
            </p>
            <div className="flex gap-4 pt-4">
              <div className="flex items-center gap-2 text-primary">
                <Rocket className="w-5 h-5" /> {t("polls:about.founded")}
              </div>
              <div className="flex items-center gap-2 text-primary">
                <Users className="w-5 h-5" /> {t("polls:about.team_size")}
              </div>
            </div>
          </div>
          <div className="relative">
            <div className="absolute inset-0 bg-primary/20 blur-3xl rounded-full"></div>
            <div className="relative bg-card border border-border p-8 rounded-2xl shadow-xl">
              <div className="flex items-center gap-4 mb-6">
                <Calendar className="w-10 h-10 text-primary" />
                <div>
                  <div className="font-bold text-foreground">DooDates</div>
                  <div className="text-sm text-muted-foreground">
                    {t("polls:about.first_tool")}
                  </div>
                </div>
              </div>
              <div className="space-y-3">
                <div className="h-2 bg-muted rounded w-3/4"></div>
                <div className="h-2 bg-muted rounded w-1/2"></div>
                <div className="h-2 bg-muted rounded w-full"></div>
              </div>
              <div className="mt-6">
                <p className="text-muted-foreground text-sm">
                  {t("polls:about.product_tagline")}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* CTA */}
      <div className="container mx-auto px-4 pb-24 text-center">
        <h2 className="text-2xl font-bold text-foreground mb-8">
          {t("polls:about.cta_title")}
        </h2>
        <div className="flex justify-center gap-4">
          <Link to="/">
            <Button
              size="lg"
              className="bg-primary hover:bg-primary/90 text-primary-foreground px-8"
              data-testid="about-commencer-maintenant"
            >
              {t("polls:create_poll")}
            </Button>
          </Link>
        </div>
      </div>

      <PublicFooter />
    </div>
  );
}
